import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Security as SecurityIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Person as PersonIcon,
  RequestPage as RequestIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { API_URL, AUTHORITY } from '../../config/api';

const AccessProvisioningTab = () => {
  const { tokenManager, user } = useAuth();

  // Azure AD App Registration details per platform (Option B).
  // Each platform team registers their app in the Divergent Insights Azure AD tenant
  // and provides their Client ID + callback URL.
  // Set these via REACT_APP_* env vars once the platform teams share their details.
  const OAUTH_PLATFORMS = {
    osmSaas: {
      clientId:    process.env.REACT_APP_OSM_SAAS_CLIENT_ID,
      callbackUrl: process.env.REACT_APP_OSM_SAAS_CALLBACK_URL,
    },
    diveInPanel: {
      clientId:    process.env.REACT_APP_DIVEIN_PANEL_CLIENT_ID,
      callbackUrl: process.env.REACT_APP_DIVEIN_PANEL_CALLBACK_URL,
    },
    panelRecruitment: {
      clientId:    process.env.REACT_APP_PANEL_RECRUITMENT_CLIENT_ID,
      callbackUrl: process.env.REACT_APP_PANEL_RECRUITMENT_CALLBACK_URL,
    },
    itSupportTicketing: {
      clientId:    process.env.REACT_APP_IT_SUPPORT_CLIENT_ID,
      callbackUrl: process.env.REACT_APP_IT_SUPPORT_CALLBACK_URL,
    },
    taskAssigner: {
      clientId:    process.env.REACT_APP_TASK_ASSIGNER_CLIENT_ID,
      callbackUrl: process.env.REACT_APP_TASK_ASSIGNER_CALLBACK_URL,
    },
  };
  const [accessData, setAccessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [rolesData, setRolesData] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [roleLoading, setRoleLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  // All available platforms with role-based configuration
  const allPlatforms = [
    // Online-specific
    {
      key: 'osm',
      label: 'OSM',
      fullName: 'Operations and Service Management',
      icon: <BusinessIcon />,
      color: 'primary',
      role: 'online'
    },
    {
      key: 'osmSaas',
      label: 'OSM SAAS',
      fullName: 'OSM Software as a Service',
      icon: <BusinessIcon />,
      color: 'primary',
      role: 'online'
    },
    {
      key: 'diveInPanel',
      label: 'Divein Panel',
      fullName: 'Dive In Panel System',
      icon: <SecurityIcon />,
      color: 'secondary',
      role: 'online'
    },
    {
      key: 'gmos',
      label: 'GMOS',
      fullName: 'Marketplace for Online Sampling',
      icon: <SecurityIcon />,
      color: 'secondary',
      role: 'online'
    },
    {
      key: 'panelRecruitment',
      label: 'Panel Recruitment',
      fullName: 'Panel Recruitment System',
      icon: <InventoryIcon />,
      color: 'success',
      role: 'online'
    },
    // Common for both online and offline
    {
      key: 'nonProjectExpense',
      label: 'Non Project Expense',
      fullName: 'Non Project Expense Management',
      icon: <InventoryIcon />,
      color: 'success',
      role: 'both'
    },
    {
      key: 'itSupportTicketing',
      label: 'IT Support (Ticket Raising)',
      fullName: 'Ticket Raising & Support System',
      icon: <PersonIcon />,
      color: 'warning',
      role: 'both'
    },
    // Offline-specific
    {
      key: 'erp',
      label: 'ERP',
      fullName: 'Enterprise Resource Planning',
      icon: <BusinessIcon />,
      color: 'primary',
      role: 'offline'
    },
    {
      key: 'taskAssigner',
      label: 'Task Assigner',
      fullName: 'Task Assignment and Management',
      icon: <InventoryIcon />,
      color: 'success',
      role: 'offline'
    }
  ];

  // Get platforms based on user's role
  const getPlatformsForUserRole = () => {
    if (!user || !user.roles) return [];
    
    if (user.roles.includes('online')) {
      return allPlatforms.filter(p => p.role === 'online' || p.role === 'both');
    } else if (user.roles.includes('offline')) {
      return allPlatforms.filter(p => p.role === 'offline' || p.role === 'both');
    }
    return [];
  };

  const platforms = getPlatformsForUserRole();

  useEffect(() => {
    if (user?.id) {
      fetchAccessProvisioning();
    }
  }, [user?.id]);

  const fetchAccessProvisioning = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/access-provisioning/${user.id}`, {
        headers: {
          ...tokenManager.getAuthHeader(),
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAccessData(result.data);
        } else {
          setError('Failed to fetch access provisioning data');
        }
      } else {
        setError('Failed to fetch access provisioning data');
      }
    } catch (error) {
      console.error('Error fetching access provisioning:', error);
      setError('Failed to fetch access provisioning data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Azure AD SSO — Option B (per-platform App Registration).
   *
   * Each platform has its own Azure AD App Registration in the Divergent Insights
   * tenant. ESHP opens Microsoft's authorize endpoint directly with:
   *   - prompt=none   → fully silent, no login screen shown
   *   - login_hint    → pre-fills the employee's email so Microsoft skips the
   *                      account picker (employee is already signed in)
   *
   * Microsoft then redirects the browser to the platform's registered callback URL
   * with an authorization code. The platform exchanges the code for tokens
   * and creates the user session — no ESHP involvement after the redirect.
   */
  const handleOAuthLogin = (platform) => {
    const config = OAUTH_PLATFORMS[platform.key];

    if (!config?.clientId || !config?.callbackUrl) {
      setError(
        `${platform.label} Azure AD integration is not yet configured. ` +
        `Please ask the ${platform.label} team to register in Azure AD and ` +
        `provide their Client ID and callback URL.`
      );
      return;
    }

    const params = new URLSearchParams({
      client_id:     config.clientId,
      response_type: 'code',
      redirect_uri:  config.callbackUrl,
      scope:         'openid email profile',
      login_hint:    accessData.email,  // employee already signed in — skips account picker
      prompt:        'none',            // fully silent — no Microsoft login screen shown
    });

    // AUTHORITY = 'https://login.microsoftonline.com/{tenantId}'
    const authorizeUrl = `${AUTHORITY}/oauth2/v2.0/authorize?${params.toString()}`;
    window.open(authorizeUrl, '_blank');
  };

  const handlePlatformClick = async (platform) => {
    if (!accessData || !accessData[platform.key] || !accessData.email) {
      setError('User data not available. Please try refreshing the page.');
      return;
    }

    // Handle Non Project Expense
    if (platform.key === 'nonProjectExpense') {
      try {
        console.log('=== NON PROJECT EXPENSE REDIRECT DEBUG ===');
        console.log('User ID:', user.id);
        console.log('API URL:', API_URL);
        console.log('Request endpoint:', `${API_URL}/access-provisioning/expense-redirect`);
        
        const response = await fetch(`${API_URL}/access-provisioning/expense-redirect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...tokenManager.getAuthHeader(),
          },
          body: JSON.stringify({
            userId: user.id
          })
        });

        console.log('Backend Response Status:', response.status);
        console.log('Backend Response OK:', response.ok);

        if (response.ok) {
          const result = await response.json();
          console.log('Backend Response Data:', result);
          if (result.success) {
            console.log('Redirect URL:', result.data.redirectUrl);
            console.log('Opening in new tab...');
            window.open(result.data.redirectUrl, '_blank');
          } else {
            setError('Failed to get Non Project Expense redirect URL');
          }
        } else {
          const errorData = await response.text();
          console.log('Backend Error Response:', errorData);
          setError('Failed to get Non Project Expense redirect URL');
        }
      } catch (error) {
        console.error('Error getting Non Project Expense redirect URL:', error);
        setError('Failed to get Non Project Expense redirect URL');
      }
      console.log('==========================================');
      return;
    }

    // Handle OSM
    if (platform.key === 'osm') {
      try {
        const response = await fetch(`${API_URL}/access-provisioning/osm-redirect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...tokenManager.getAuthHeader(),
          },
          body: JSON.stringify({
            userId: user.id
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            window.open(result.data.redirectUrl, '_blank');
          } else {
            setError('Failed to get OSM redirect URL');
          }
        } else {
          setError('Failed to get OSM redirect URL');
        }
      } catch (error) {
        console.error('Error getting OSM redirect URL:', error);
        setError('Failed to get OSM redirect URL');
      }
      return;
    }

    // Handle OSM SAAS — Azure AD SSO (Option B)
    if (platform.key === 'osmSaas') {
      handleOAuthLogin(platform);
      return;
    }

    // Handle Divein Panel — Azure AD SSO (Option B)
    if (platform.key === 'diveInPanel') {
      handleOAuthLogin(platform);
      return;
    }

    // Handle GMOS
    if (platform.key === 'gmos') {
      try {
        const response = await fetch(`${API_URL}/access-provisioning/gmos-redirect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...tokenManager.getAuthHeader(),
          },
          body: JSON.stringify({
            userId: user.id
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            window.open(result.redirectUrl, '_blank');
          } else {
            setError('Failed to get GMOS redirect URL');
          }
        } else {
          setError('Failed to get GMOS redirect URL');
        }
      } catch (error) {
        console.error('Error getting GMOS redirect URL:', error);
        setError('Failed to get GMOS redirect URL');
      }
      return;
    }

    // Handle Panel Recruitment — Azure AD SSO (Option B)
    if (platform.key === 'panelRecruitment') {
      handleOAuthLogin(platform);
      return;
    }

    // Handle IT Support Ticketing — Azure AD SSO (Option B)
    if (platform.key === 'itSupportTicketing') {
      handleOAuthLogin(platform);
      return;
    }

    // Handle Task Assigner — Azure AD SSO (Option B)
    if (platform.key === 'taskAssigner') {
      handleOAuthLogin(platform);
      return;
    }

    // Handle ERP and other platforms with role selection
    setSelectedPlatform(platform);
    setRoleLoading(true);
    setSelectedRole('');

    try {
      // Get available roles from ERP API
      const response = await fetch(`${API_URL}/access-provisioning/get-roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...tokenManager.getAuthHeader(),
        },
        body: JSON.stringify({
          email: accessData.email
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          // Check if there's only one role available
          const roles = result.data.role ? result.data.role.split(',') : [];
          
          if (roles.length === 1) {
            // Only one role available - redirect directly without dialog
            const singleRole = roles[0].trim();
            try {
              const redirectResponse = await fetch(`${API_URL}/access-provisioning/erp/verify-redirect`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...tokenManager.getAuthHeader(),
                },
                body: JSON.stringify({
                  email: accessData.email,
                  user_type: singleRole
                })
              });

              if (redirectResponse.ok) {
                const redirectResult = await redirectResponse.json();
                if (redirectResult.success) {
                  // Close loading and redirect to platform
                  setRoleLoading(false);
                  window.open(redirectResult.data.redirectUrl, '_blank');
                  return;
                }
              }
            } catch (redirectError) {
              console.error('Error getting redirect URL:', redirectError);
            }
          }
          
          // Multiple roles or redirect failed - show dialog
          setRolesData(result.data);
          setRoleDialogOpen(true);
        } else {
          setError(result.message || 'Failed to fetch roles from ERP');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || `Failed to fetch roles from ERP (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('Error fetching ERP roles:', error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setError('Network error: Unable to connect to ERP system. Please check your connection and try again.');
      } else {
        setError(`Failed to fetch roles from ERP: ${error.message}`);
      }
    } finally {
      setRoleLoading(false);
    }
  };

  const handleRoleSelection = (roleId) => {
    setSelectedRole(roleId);
  };

  const handleRoleSubmit = async () => {
    if (!selectedRole || !accessData?.email) {
      setError('Please select a role');
      return;
    }

    try {
      // Get redirect URL and open in new tab
      const response = await fetch(`${API_URL}/access-provisioning/erp/verify-redirect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...tokenManager.getAuthHeader(),
        },
        body: JSON.stringify({
          email: accessData.email,
          user_type: selectedRole
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Close dialog and redirect to platform
          setRoleDialogOpen(false);
          setSelectedPlatform(null);
          setRolesData(null);
          setSelectedRole('');
          
          // Open the platform in new tab
          window.open(result.data.redirectUrl, '_blank');
        } else {
          setError('Failed to get redirect URL');
        }
      } else {
        setError('Failed to get redirect URL');
      }
    } catch (error) {
      console.error('Error getting redirect URL:', error);
      setError('Failed to get redirect URL');
    }
  };

  const handleCloseDialog = () => {
    setRoleDialogOpen(false);
    setSelectedPlatform(null);
    setRolesData(null);
    setSelectedRole('');
  };

  const handleRequestAccess = async (platformKey) => {
    if (!user?.id) {
      setError('User information not available');
      return;
    }

    if (!platformKey) {
      setError('Platform not specified');
      return;
    }

    const platform = platforms.find(p => p.key === platformKey);
    if (!platform) {
      setError('Invalid platform selected');
      return;
    }

    setRequestLoading(true);
    try {
      const response = await fetch(`${API_URL}/notifications/access-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...tokenManager.getAuthHeader(),
        },
        body: JSON.stringify({
          userId: user.id,
          platform: platform.key,
          platformName: platform.label,
          message: `Requesting access to ${platform.label} (${platform.fullName})`
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRequestSuccess(true);
          setError(null);
        } else {
          setError(result.message || 'Failed to send access request');
        }
      } else {
        setError('Failed to send access request');
      }
    } catch (error) {
      console.error('Error sending access request:', error);
      setError('Failed to send access request');
    } finally {
      setRequestLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Error Alert - Show at top if there's an error */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        System Access Provisioning
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Click on the enabled platforms to access your accounts. Disabled platforms indicate access has not been provisioned yet.
      </Typography>


      {/* Platform Grid - Always show platforms, even if there's an error */}
      <Grid container spacing={3}>
        {platforms.map((platform) => {
          const isEnabled = accessData && accessData[platform.key];
          
          return (
            <Grid item xs={12} sm={6} md={4} key={platform.key}>
           <Card
  sx={{
    cursor: isEnabled ? "pointer" : "default",
    transition: "all 0.3s ease",
    "&:hover": isEnabled
      ? {
          transform: "translateY(-4px)",
          boxShadow: 4,
        }
      : {},
    border: isEnabled
      ? `2px solid ${platform.color}.main`
      : "1px solid #e0e0e0",
  }}
  onClick={() => isEnabled && handlePlatformClick(platform)}
>
  <CardContent sx={{ textAlign: "center", py: 3 }}>
    <Box
      sx={{
        color: isEnabled ? `${platform.color}.main` : "text.disabled",
        mb: 2,
        fontSize: "2rem",
        opacity: isEnabled ? 1 : 0.5, // dim only icon
      }}
    >
      {platform.icon}
    </Box>

    <Typography
      variant="h6"
      gutterBottom
      sx={{ fontWeight: 600, opacity: isEnabled ? 1 : 0.5 }}
    >
      {platform.label}
    </Typography>

    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 2, opacity: isEnabled ? 1 : 0.5 }}
    >
      {platform.fullName}
    </Typography>

    {/* Status */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
        mb: 2,
        opacity: isEnabled ? 1 : 0.5,
      }}
    >
      {isEnabled ? (
        <>
          <CheckCircleIcon color="success" fontSize="small" />
          <Chip
            label="Access Granted"
            size="small"
            color="success"
            variant="outlined"
          />
        </>
      ) : (
        <>
          <BlockIcon color="disabled" fontSize="small" />
          <Chip
            label="Access Pending"
            size="small"
            color="default"
            variant="outlined"
          />
        </>
      )}
    </Box>
    {/* <br /> */}

    {/* Request button stays bright even if card is disabled */}
    {!isEnabled && (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleRequestAccess(platform.key);
        }}
      >
        Request For Access 
      </Button>
    )}
  </CardContent>
</Card>

            </Grid>
          );
        })}
      </Grid>


      {/* No Access Data Message */}
      {!accessData && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="info" gutterBottom>
            Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Access provisioning data is not available yet. The platforms above show the available systems. Contact your administrator to request access.
          </Typography>
        </Box>
      )}

      {/* Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Access Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {accessData ? Object.values(platforms).filter(p => accessData && accessData[p.key]).length : 0} of {platforms.length} platforms are accessible
        </Typography>
      </Box>

      {/* Role Selection Dialog */}
      <Dialog 
        open={roleDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Your Role - {selectedPlatform?.label}
        </DialogTitle>
        <DialogContent>
          {roleLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : rolesData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please select your role for {selectedPlatform?.label}:
              </Typography>
              
              <FormControl>
                <RadioGroup
                  value={selectedRole}
                  onChange={(e) => handleRoleSelection(e.target.value)}
                >
                  {rolesData && rolesData.role ? 
                    rolesData.role.split(',').map((roleId, index) => {
                      const roleName = rolesData.role_names ? 
                        rolesData.role_names.split(',')[index]?.trim() : 
                        roleId;
                      return (
                        <FormControlLabel
                          key={roleId}
                          value={roleId}
                          control={<Radio />}
                          label={roleName}
                        />
                      );
                    })
                  : (
                    <Typography color="error">
                      No roles available
                    </Typography>
                  )}
                </RadioGroup>
              </FormControl>

              {selectedRole && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.main">
                    Selected: {rolesData && rolesData.role_names ? 
                      rolesData.role_names.split(',').find((_, index) => 
                        rolesData.role.split(',')[index] === selectedRole
                      )?.trim() : 
                      selectedRole
                    }
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="error">
              Failed to load roles
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleRoleSubmit}
            disabled={!selectedRole || roleLoading}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      <Snackbar
        open={requestSuccess}
        autoHideDuration={6000}
        onClose={() => setRequestSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setRequestSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Access request sent successfully! Admin will be notified.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccessProvisioningTab;
